/**
 * OpenAI Realtime API service using WebRTC for client-side audio processing
 * This replaces the custom VAD implementation with OpenAI's built-in voice activity detection
 */
import { AUTH_BASE_URL } from '../config/api';

export class OpenAIRealtimeService {
  constructor() {
    // WebRTC connection
    this.peerConnection = null;
    this.dataChannel = null;
    this.audioElement = null;
    this.localStream = null;
    this.ephemeralToken = null;
    
    // State management
    this.isConnected = false;
    this.isInitialized = false;
    this.isAudioPlaying = false;
    this.conversation = [];
    this.conversationTurnCounter = 0; // Track conversation turns for unique IDs
    this.sessionId = null;
    this.audioNeedsEnabling = false;
    this.userHasEnabledAudio = false;
    this.audioContext = null;
    this.mediaStreamSource = null;
    this.gainNode = null;
    
    // Callbacks
    this.onMessageCallback = null;
    this.onErrorCallback = null;
    this.onStatusCallback = null;
    this.onAudioStartCallback = null;
    this.onAudioEndCallback = null;
    
    // Session configuration
    this.selectedModel = 'gpt-4o-realtime-preview-2024-12-17';
    this.selectedVoice = 'alloy';
    this.currentUser = null;
    
    // Advanced session settings
    this.sessionConfig = {
      temperature: 0.8,
      max_response_output_tokens: 1024,
      turn_detection: {
        type: 'server_vad',
        threshold: 0.7, // Higher threshold to reduce false triggers
        prefix_padding_ms: 300,
        silence_duration_ms: 1500, // Longer silence required
        create_response: true
      },
      input_audio_format: 'pcm16',
      output_audio_format: 'pcm16',
      input_audio_transcription: {
        model: 'whisper-1'
      }
    };
    
    // Event tracking
    this.lastEventId = 0;
    this.pendingResponses = new Map();
    this.truncatedItems = new Set(); // Track items that have been truncated
    this.completedItems = new Set(); // Track items that have been fully processed
  }

  async initialize(onMessage, onError, onStatusChange, onAudioStart, onAudioEnd) {
    console.log('🚀 Initializing OpenAI Realtime API service...');
    
    // Prevent re-initialization if already initialized
    if (this.isInitialized && this.peerConnection && this.peerConnection.connectionState === 'connected') {
      console.log('🚀 Service already initialized and connected, updating callbacks only');
      this.onMessageCallback = onMessage;
      this.onErrorCallback = onError;
      this.onStatusCallback = onStatusChange;
      this.onAudioStartCallback = onAudioStart;
      this.onAudioEndCallback = onAudioEnd;
      this.onStatusCallback?.('ready');
      return true;
    }
    
    this.onMessageCallback = onMessage;
    this.onErrorCallback = onError;
    this.onStatusCallback = onStatusChange;
    this.onAudioStartCallback = onAudioStart;
    this.onAudioEndCallback = onAudioEnd;
    
    try {
      this.onStatusCallback?.('initializing');
      
      // Step 1: Generate ephemeral token
      await this.generateEphemeralToken();
      
      // Step 2: Set up WebRTC peer connection
      await this.setupWebRTCConnection();
      
      // Step 3: Set up local audio input
      await this.setupLocalAudio();
      
      // Step 4: Establish connection to OpenAI Realtime API
      await this.connectToOpenAI();
      
      this.isInitialized = true;
      this.onStatusCallback?.('ready');
      
      console.log('✅ OpenAI Realtime API service initialized successfully');
      return true;
      
    } catch (error) {
      console.error('❌ Failed to initialize OpenAI Realtime API:', error);
      this.onErrorCallback?.(error);
      this.onStatusCallback?.('error');
      return false;
    }
  }

  async generateEphemeralToken() {
    console.log('🔑 Generating ephemeral token...');
    
    try {
      const response = await fetch(`${AUTH_BASE_URL}/api/audio/ephemeral-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          model: this.selectedModel  // This will now be the mapped/compatible model
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to generate ephemeral token: ${response.status}`);
      }

      const data = await response.json();
      this.ephemeralToken = data.clientSecret;
      this.sessionId = data.sessionId;
      
      console.log('✅ Ephemeral token generated:', {
        sessionId: this.sessionId,
        expiresAt: data.expiresAt,
        model: data.model
      });
      
    } catch (error) {
      throw new Error(`Token generation failed: ${error.message}`);
    }
  }

  getInstructions() {
    const firstName = this.currentUser?.name?.split(' ')[0] || this.currentUser?.email?.split('@')[0] || '';
    
    return `You are Claude, a friendly and helpful AI assistant created by Anthropic. You're having a natural voice conversation with ${firstName || 'the user'}. Be warm, conversational, and engaging. Use short, clear sentences that are easy to follow when spoken. Avoid complex punctuation, markdown formatting, or special characters. Keep responses concise - aim for 1-3 sentences maximum. Speak naturally as if talking to someone in person.`;
  }

  async setupWebRTCConnection() {
    console.log('🌐 Setting up WebRTC peer connection...');
    
    try {
      // Create RTCPeerConnection
      this.peerConnection = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' }
        ]
      });

      // Set up event handlers
      this.peerConnection.onconnectionstatechange = () => {
        console.log('WebRTC connection state:', this.peerConnection.connectionState);
        
        if (this.peerConnection.connectionState === 'connected') {
          this.isConnected = true;
          this.onStatusCallback?.('ready');
        } else if (this.peerConnection.connectionState === 'disconnected' || 
                   this.peerConnection.connectionState === 'failed') {
          this.isConnected = false;
          this.onStatusCallback?.('error');
          this.onErrorCallback?.(new Error('WebRTC connection lost'));
        }
      };

      // Set up data channel for OpenAI Realtime events
      this.dataChannel = this.peerConnection.createDataChannel('oai-events');
      this.dataChannel.addEventListener('open', () => {
        console.log('✅ Data channel opened');
      });

      this.dataChannel.addEventListener('message', (event) => {
        this.handleServerEvent(JSON.parse(event.data));
      });

      this.dataChannel.addEventListener('error', (error) => {
        console.error('❌ Data channel error:', error);
        this.onErrorCallback?.(new Error('Data channel error'));
      });

      // Set up remote audio stream handling
      this.setupRemoteAudioHandling();
      
    } catch (error) {
      throw new Error(`WebRTC setup failed: ${error.message}`);
    }
  }

  setupRemoteAudioHandling() {
    // Create audio element for playing OpenAI's voice responses
    this.audioElement = document.createElement('audio');
    this.audioElement.autoplay = true;
    this.audioElement.playsInline = true;
    
    // Handle remote audio tracks from OpenAI
    this.peerConnection.ontrack = (event) => {
      console.log('🔊 Received remote audio track from OpenAI');
      
      if (event.streams && event.streams[0]) {
        console.log('🔊 Setting up audio stream for playback');
        
        // Check if stream has audio tracks and handle muted tracks differently
        const audioTracks = event.streams[0].getAudioTracks();
        console.log('🔊 Audio tracks in stream:', audioTracks.length, audioTracks);
        
        if (audioTracks.length === 0) {
          console.warn('🔊 No audio tracks found in stream');
          return;
        }
        
        // Log track information and detect muted tracks
        let hasMutedTracks = false;
        audioTracks.forEach((track, index) => {
          console.log(`🔊 Audio track ${index}:`, {
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
            kind: track.kind,
            label: track.label
          });
          
          track.enabled = true;
          if (track.muted) {
            hasMutedTracks = true;
            console.warn(`🔊 Audio track ${index} is muted at source level - using AudioContext workaround`);
          }
        });
        
        // Create AudioContext to handle WebRTC audio, especially for muted tracks
        if (!this.audioContext) {
          try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            console.log('🔊 Created AudioContext for WebRTC audio handling');
          } catch (error) {
            console.warn('🔊 Could not create AudioContext:', error);
            // Fallback to regular audio element
            this.setupFallbackAudioElement(event.streams[0]);
            return;
          }
        }
        
        // Handle muted tracks using AudioContext bypass
        if (hasMutedTracks && this.audioContext) {
          console.log('🔊 Using AudioContext approach to bypass muted tracks');
          this.setupAudioContextPlayback(event.streams[0]);
        } else {
          console.log('🔊 Using standard audio element approach');
          this.setupFallbackAudioElement(event.streams[0]);
        }
      }
    };
  }
  
  setupAudioContextPlayback(stream) {
    try {
      // Clean up existing source
      if (this.mediaStreamSource) {
        this.mediaStreamSource.disconnect();
        this.mediaStreamSource = null;
      }
      
      // Create MediaStreamSource from the WebRTC stream
      this.mediaStreamSource = this.audioContext.createMediaStreamSource(stream);
      
      // Create a gain node for volume control
      if (!this.gainNode) {
        this.gainNode = this.audioContext.createGain();
        this.gainNode.gain.value = 1.0; // Full volume
      }
      
      // Connect: MediaStreamSource -> GainNode -> Destination
      this.mediaStreamSource.connect(this.gainNode);
      this.gainNode.connect(this.audioContext.destination);
      
      console.log('🔊 Connected WebRTC stream to AudioContext with bypass for muted tracks');
      
      // Resume AudioContext if suspended
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume().then(() => {
          console.log('🔊 AudioContext resumed for WebRTC audio');
          this.audioNeedsEnabling = false;
          this.userHasEnabledAudio = true;
          this.notifyAudioStart();
        }).catch(error => {
          console.warn('🔊 Could not resume AudioContext:', error);
          this.audioNeedsEnabling = true;
        });
      } else {
        console.log('🔊 AudioContext is ready, audio should play through it');
        this.audioNeedsEnabling = false;
        this.userHasEnabledAudio = true;
        
        // Set a small delay before notifying audio start to allow WebRTC to settle
        setTimeout(() => {
          this.notifyAudioStart();
        }, 100);
      }
      
      // Also setup the audio element as a fallback but don't rely on it for playback
      this.setupFallbackAudioElement(stream, false);
      
    } catch (error) {
      console.error('🔊 Failed to set up AudioContext playback:', error);
      // Fall back to regular audio element
      this.setupFallbackAudioElement(stream);
    }
  }
  
  setupFallbackAudioElement(stream, enableAutoplay = true) {
    // Set up audio element with stream
    this.audioElement.srcObject = stream;
    this.audioElement.volume = 1.0;
    this.audioElement.muted = false;
    
    // Set up audio event listeners
    this.audioElement.onplay = () => {
      console.log('🔊 Audio element playback started');
      if (enableAutoplay) {
        this.notifyAudioStart();
      }
    };
    
    this.audioElement.oncanplay = () => {
      console.log('🔊 Audio element can play');
      if (enableAutoplay && this.audioNeedsEnabling && this.audioElement.paused) {
        console.log('🔊 Retrying audio element playback after canplay event');
        this.audioElement.play().catch(error => {
          console.warn('🔊 Still cannot autoplay audio element:', error);
        });
      }
    };
    
    this.audioElement.onloadeddata = () => {
      console.log('🔊 Audio element data loaded');
      if (enableAutoplay && this.userHasEnabledAudio && this.audioElement.paused) {
        console.log('🔊 Auto-playing audio element due to previous user enablement');
        this.audioElement.play().catch(error => {
          console.warn('🔊 Could not auto-play loaded audio element:', error);
        });
      }
    };
    
    this.audioElement.onended = () => {
      console.log('🔊 Audio element playback ended');
      if (enableAutoplay) {
        this.notifyAudioEnd();
      }
    };
    
    this.audioElement.onpause = () => {
      console.log('🔊 Audio element playback paused');
      if (enableAutoplay) {
        this.notifyAudioEnd();
      }
    };
    
    this.audioElement.onerror = (error) => {
      console.error('🔊 Audio element playback error:', error);
      if (enableAutoplay) {
        this.notifyAudioEnd();
      }
    };
    
    if (enableAutoplay) {
      // Try to play immediately when audio data is available
      this.audioElement.play().then(() => {
        console.log('🔊 Audio element playback started successfully');
        this.audioNeedsEnabling = false;
        this.userHasEnabledAudio = true;
      }).catch((error) => {
        console.warn('🔊 Audio element autoplay prevented:', error);
        this.audioNeedsEnabling = true;
        
        // Attempt to enable audio automatically for better UX
        setTimeout(() => {
          this.enableAudio();
        }, 100);
      });
    }
    
    // Listen for stream end
    stream.addEventListener('ended', () => {
      console.log('🔊 Audio stream ended');
      if (enableAutoplay) {
        this.notifyAudioEnd();
      }
    });
    
    console.log('🔊 MediaStream state:', {
      active: stream.active,
      id: stream.id,
      trackCount: stream.getTracks().length
    });
  }
  
  notifyAudioStart() {
    if (!this.isAudioPlaying) {
      this.isAudioPlaying = true;
      this.onStatusCallback?.('playing');
      this.onAudioStartCallback?.();
    }
  }
  
  notifyAudioEnd() {
    if (this.isAudioPlaying) {
      this.isAudioPlaying = false;
      this.onStatusCallback?.('ready');
      this.onAudioEndCallback?.();
    }
  }

  async setupLocalAudio() {
    console.log('🎤 Setting up local audio input...');
    
    try {
      // Request microphone access
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 24000, // OpenAI Realtime API prefers 24kHz
        }
      });

      // Add local audio track to peer connection
      const audioTrack = this.localStream.getAudioTracks()[0];
      this.peerConnection.addTrack(audioTrack, this.localStream);
      
      console.log('✅ Local audio track added to peer connection');
      
    } catch (error) {
      throw new Error(`Microphone access failed: ${error.message}`);
    }
  }

  async connectToOpenAI() {
    console.log('🔗 Connecting to OpenAI Realtime API...');
    
    try {
      // Create SDP offer
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      // Send offer to OpenAI Realtime API
      const baseUrl = 'https://api.openai.com/v1/realtime';
      const response = await fetch(`${baseUrl}?model=${encodeURIComponent(this.selectedModel)}`, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          'Authorization': `Bearer ${this.ephemeralToken}`,
          'Content-Type': 'application/sdp'
        },
      });

      if (!response.ok) {
        throw new Error(`OpenAI connection failed: ${response.status}`);
      }

      // Set remote description from OpenAI's answer
      const answerSdp = await response.text();
      const answer = {
        type: 'answer',
        sdp: answerSdp,
      };
      
      await this.peerConnection.setRemoteDescription(answer);
      
      console.log('✅ Connected to OpenAI Realtime API');
      
    } catch (error) {
      throw new Error(`OpenAI connection failed: ${error.message}`);
    }
  }

  // Send events to OpenAI via data channel
  sendEvent(event) {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
      console.warn('⚠️ Data channel not ready, cannot send event:', event);
      return false;
    }

    try {
      // Add event ID for tracking
      const eventWithId = {
        ...event,
        event_id: `evt_${++this.lastEventId}`
      };
      
      this.dataChannel.send(JSON.stringify(eventWithId));
      console.log('📤 Sent event to OpenAI:', eventWithId.type);
      return true;
      
    } catch (error) {
      console.error('❌ Failed to send event:', error);
      return false;
    }
  }

  // Handle events received from OpenAI
  handleServerEvent(event) {
    console.log('📥 Received event from OpenAI:', event.type, event);
    
    switch (event.type) {
      case 'session.created':
        this.handleSessionCreated(event);
        break;
        
      case 'session.updated':
        this.handleSessionUpdated(event);
        break;
        
      case 'input_audio_buffer.speech_started':
        this.handleSpeechStarted(event);
        break;
        
      case 'input_audio_buffer.speech_stopped':
        this.handleSpeechStopped(event);
        break;
        
      case 'input_audio_buffer.committed':
        this.handleInputCommitted(event);
        break;
        
      case 'conversation.item.created':
        this.handleConversationItemCreated(event);
        break;
        
      case 'response.created':
        this.handleResponseCreated(event);
        break;
        
      case 'response.done':
        this.handleResponseDone(event);
        break;
        
      case 'response.audio.delta':
        this.handleAudioDelta(event);
        break;
        
      case 'response.audio_transcript.delta':
        this.handleAudioTranscriptDelta(event);
        break;
        
      case 'response.text.delta':
        this.handleTextDelta(event);
        break;
        
      case 'response.output_item.added':
        this.handleOutputItemAdded(event);
        break;
        
      case 'response.output_item.done':
        this.handleOutputItemDone(event);
        break;
        
      case 'conversation.item.truncated':
        this.handleConversationItemTruncated(event);
        break;
        
      case 'conversation.item.deleted':
        this.handleConversationItemDeleted(event);
        break;
        
      case 'input_audio_buffer.cleared':
        this.handleInputAudioBufferCleared(event);
        break;
        
      case 'conversation.item.input_audio_transcription.completed':
        console.log('🎯 INPUT AUDIO TRANSCRIPTION COMPLETED:', event);
        this.handleInputAudioTranscriptionCompleted(event);
        break;
        
      case 'conversation.item.input_audio_transcription.failed':
        console.log('❌ INPUT AUDIO TRANSCRIPTION FAILED:', event);
        this.handleInputAudioTranscriptionFailed(event);
        break;
        
      case 'rate_limits.updated':
        this.handleRateLimitsUpdated(event);
        break;
        
      case 'error':
        this.handleError(event);
        break;
        
      default:
        console.log('🔍 Unhandled event type:', event.type);
    }
  }

  handleSessionCreated(event) {
    console.log('✅ Session created:', event.session);
    
    // Update session with current configuration
    this.updateSession();
    
    // Add existing conversation history to the session
    this.addHistoryToSession();
  }

  handleSessionUpdated(event) {
    console.log('✅ Session updated:', event.session);
  }

  handleSpeechStarted(event) {
    console.log('🎤 Speech started detected by OpenAI VAD');
    this.onStatusCallback?.('recording');
    
    // Stop any currently playing audio to allow interruption
    if (this.audioElement && !this.audioElement.paused) {
      this.audioElement.pause();
      this.onAudioEndCallback?.();
    }
    
    // If user interrupts while assistant is responding, mark pending responses as potentially truncated
    if (this.pendingResponses.size > 0) {
      console.log('🎤 User interrupt detected - assistant responses may be truncated');
      for (const [responseId, response] of this.pendingResponses) {
        response.status = 'interrupted';
      }
    }
  }

  handleSpeechStopped(event) {
    console.log('🔇 Speech stopped detected by OpenAI VAD', event);
    this.onStatusCallback?.('processing');
    
    // Audio input will be automatically committed by the server
    // No need to manually trigger response creation as turn_detection handles this
  }

  handleInputCommitted(event) {
    console.log('✅ Audio input committed:', event);
    
    // Create a user message for the committed audio - this will be replaced by transcription
    if (event.item_id) {
      this.conversationTurnCounter++;
      const userMessage = {
        role: 'user',
        content: '[Voice Input]',
        timestamp: Date.now(),
        id: `user_turn_${this.conversationTurnCounter}_${Date.now()}`, // Unique ID for each turn
        originalItemId: event.item_id, // Keep original for reference
        isRealtime: true,
        audio: true,
        isStreaming: true // Mark as streaming so it gets replaced by transcription
      };
      
      console.log('🎤 DEBUG: Creating new user message for committed audio');
      console.log('🎤 DEBUG: Turn counter:', this.conversationTurnCounter);
      console.log('🎤 DEBUG: Original item_id:', event.item_id);
      console.log('🎤 DEBUG: New unique ID:', userMessage.id);
      console.log('🎤 DEBUG: Current conversation length before:', this.conversation.length);
      
      this.conversation.push(userMessage);
      
      console.log('🎤 DEBUG: Current conversation length after:', this.conversation.length);
      console.log('🎤 DEBUG: All conversation IDs:', this.conversation.map(m => `${m.role}:${m.id}`));
      
      this.onMessageCallback?.(userMessage);
    }
  }

  handleConversationItemCreated(event) {
    console.log('💬 Conversation item created:', event.item);
    
    // Only handle user items here, assistant items are handled in output events
    if (event.item.role === 'user') {
      const message = {
        role: event.item.role,
        content: this.extractContentFromItem(event.item),
        timestamp: Date.now(),
        id: event.item.id,
        isRealtime: true
      };

      this.conversation.push(message);
      this.onMessageCallback?.(message);
    }
  }

  handleResponseCreated(event) {
    console.log('🤖 Response created:', event.response);
    this.onStatusCallback?.('thinking');
    
    // Track the response for potential streaming updates
    this.pendingResponses.set(event.response.id, {
      id: event.response.id,
      status: 'thinking',
      items: []
    });
  }

  handleResponseDone(event) {
    console.log('✅ Response done:', event.response);
    
    // Clean up the pending response
    this.pendingResponses.delete(event.response.id);
    
    // Check if we should return to ready state
    if (this.pendingResponses.size === 0) {
      this.onStatusCallback?.('ready');
    }
  }

  handleAudioDelta(event) {
    // Audio is handled via WebRTC tracks, but we can track progress here
    console.log('🔊 Audio delta received (via WebRTC)');
    
    // If user has enabled audio before, try to ensure it's playing
    if (this.userHasEnabledAudio && this.audioElement && this.audioElement.paused) {
      console.log('🔊 User has enabled audio before, attempting to play');
      this.audioElement.play().catch(error => {
        console.warn('🔊 Could not start audio playback:', error);
      });
    }
    
    // Notify that audio playback is happening
    if (!this.isAudioPlaying) {
      this.isAudioPlaying = true;
      this.onStatusCallback?.('playing');
      this.onAudioStartCallback?.();
    }
  }

  handleAudioTranscriptDelta(event) {
    console.log('📝 Audio transcript delta:', event.delta);
    
    // Update the current streaming message with transcript
    if (event.item_id && event.delta) {
      // Find existing message or create new one
      let message = this.conversation.find(msg => msg.originalItemId === event.item_id);
      
      console.log('🤖 DEBUG: Looking for assistant message with originalItemId:', event.item_id);
      console.log('🤖 DEBUG: Found existing assistant message:', !!message);
      
      if (!message) {
        this.conversationTurnCounter++;
        message = {
          role: 'assistant',
          content: '',
          timestamp: Date.now(),
          id: `assistant_turn_${this.conversationTurnCounter}_${Date.now()}`, // Unique ID for each turn
          originalItemId: event.item_id, // Keep original for reference
          isRealtime: true,
          isStreaming: true
        };
        
        console.log('🤖 DEBUG: Creating new assistant message');
        console.log('🤖 DEBUG: Turn counter:', this.conversationTurnCounter);
        console.log('🤖 DEBUG: New assistant ID:', message.id);
        console.log('🤖 DEBUG: Conversation length before:', this.conversation.length);
        
        this.conversation.push(message);
        
        console.log('🤖 DEBUG: Conversation length after:', this.conversation.length);
        console.log('🤖 DEBUG: All conversation IDs:', this.conversation.map(m => `${m.role}:${m.id}`));
      } else {
        console.log('🤖 DEBUG: Using existing assistant message:', message.id);
      }
      
      // Append transcript delta
      message.content += event.delta;
      message.isStreaming = true;
      
      this.onMessageCallback?.(message);
    }
  }

  handleTextDelta(event) {
    console.log('📝 Text delta:', event.delta);
    
    // Handle streaming text responses for text-only outputs
    if (event.item_id && event.delta) {
      let message = this.conversation.find(msg => msg.originalItemId === event.item_id);
      
      if (!message) {
        this.conversationTurnCounter++;
        message = {
          role: 'assistant',
          content: '',
          timestamp: Date.now(),
          id: `assistant_turn_${this.conversationTurnCounter}_${Date.now()}`, // Unique ID for each turn
          originalItemId: event.item_id, // Keep original for reference
          isRealtime: true,
          isStreaming: true
        };
        this.conversation.push(message);
      }
      
      message.content += event.delta;
      message.isStreaming = true;
      
      this.onMessageCallback?.(message);
    }
  }

  handleOutputItemAdded(event) {
    console.log('📝 Output item added:', event.item);
    
    // Handle new output items (audio, text, etc.)
    if (event.item.type === 'message') {
      const message = {
        role: 'assistant',
        content: this.extractContentFromItem(event.item),
        timestamp: Date.now(),
        id: event.item.id,
        isRealtime: true,
        isStreaming: true
      };
      
      this.onMessageCallback?.(message);
    }
  }

  handleOutputItemDone(event) {
    console.log('✅ Output item done:', event.item);
    
    // Create a new completed assistant message
    if (event.item.type === 'message') {
      // Check if this item was truncated/interrupted - if so, don't send to main chat
      const wasTruncated = this.truncatedItems.has(event.item.id);
      const alreadyCompleted = this.completedItems.has(event.item.id);
      
      if (wasTruncated) {
        console.log('✂️ Skipping truncated item from main chat:', event.item.id);
        return; // Don't send truncated messages to main chat
      }
      
      if (alreadyCompleted) {
        console.log('🔄 Item already completed, skipping:', event.item.id);
        return; // Don't process the same item twice
      }
      
      // Mark as completed
      this.completedItems.add(event.item.id);
      
      // Find any existing streaming message to get the accumulated transcript
      // Look for messages by originalItemId since our custom IDs don't match OpenAI's item IDs
      const existingMessage = this.conversation.find(msg => msg.originalItemId === event.item.id);
      const transcriptContent = existingMessage ? existingMessage.content : this.extractContentFromItem(event.item);
      
      console.log('🔍 DEBUG: Looking for streaming message with item_id:', event.item.id);
      console.log('🔍 DEBUG: Found existing message:', !!existingMessage);
      console.log('🔍 DEBUG: Transcript content:', transcriptContent);
      
      // Only send to main chat if we have meaningful content
      if (!transcriptContent || transcriptContent.trim() === '' || transcriptContent === '[Audio]') {
        console.log('⚠️ Skipping empty or placeholder content:', transcriptContent);
        return;
      }
      
      // Remove any existing streaming message for this item
      this.conversation = this.conversation.filter(msg => msg.originalItemId !== event.item.id);
      
      // Create new complete message with UNIQUE ID for main chat
      const message = {
        role: 'assistant',
        content: transcriptContent, // Use accumulated transcript
        timestamp: Date.now(),
        id: `assistant_${event.item.id}_${Date.now()}`, // Unique ID for main chat
        originalItemId: event.item.id, // Keep original for voice overlay
        isRealtime: true,
        isStreaming: false, // Mark as complete so it goes to main chat
        transcription: transcriptContent,
        audio: true // Mark that this had audio
      };
      
      this.conversation.push(message);
      console.log('✅ Created new assistant message with transcript:', transcriptContent);
      console.log('✅ Calling onMessageCallback with final assistant message:', message);
      this.onMessageCallback?.(message);
    }
  }

  handleConversationItemTruncated(event) {
    console.log('✂️ Conversation item truncated:', event);
    
    // Mark this item as truncated so we don't send incomplete content to main chat
    if (event.item_id) {
      this.truncatedItems.add(event.item_id);
      console.log('✂️ Marked item as truncated:', event.item_id);
      
      // Find and update any existing message to show it was truncated
      const existingMessage = this.conversation.find(msg => 
        msg.id === event.item_id || msg.originalItemId === event.item_id
      );
      
      if (existingMessage) {
        existingMessage.content = existingMessage.content + ' [Interrupted]';
        existingMessage.isTruncated = true;
        this.onMessageCallback?.(existingMessage);
      }
    }
  }

  handleConversationItemDeleted(event) {
    console.log('🗑️ Conversation item deleted:', event);
    // Handle when a conversation item is deleted
    // Remove from local conversation if needed
    this.conversation = this.conversation.filter(msg => msg.id !== event.item_id);
  }

  handleInputAudioBufferCleared(event) {
    console.log('🧹 Input audio buffer cleared:', event);
    // Handle when the input audio buffer is cleared
    // This happens when the user's audio input is reset
  }

  handleInputAudioTranscriptionCompleted(event) {
    console.log('🎯📝 Input audio transcription completed:', event);
    
    // Create a new completed user message with transcription
    if (event.item_id && event.transcript) {
      // Check if this item was truncated or already completed
      const wasTruncated = this.truncatedItems.has(event.item_id);
      const alreadyCompleted = this.completedItems.has(event.item_id);
      
      if (wasTruncated) {
        console.log('✂️ Skipping truncated user input from main chat:', event.item_id);
        return;
      }
      
      if (alreadyCompleted) {
        console.log('🔄 User input already completed, skipping:', event.item_id);
        return;
      }
      
      // Only process if transcript is meaningful
      if (!event.transcript.trim() || event.transcript.trim().length < 2) {
        console.log('⚠️ Skipping empty or too short transcript:', event.transcript);
        return;
      }
      
      // Mark as completed
      this.completedItems.add(event.item_id);
      
      // Find and update the existing placeholder message for this item
      console.log('📝 DEBUG: Looking for message with originalItemId:', event.item_id);
      console.log('📝 DEBUG: Current conversation messages:', this.conversation.map(m => ({
        role: m.role, 
        id: m.id, 
        originalItemId: m.originalItemId, 
        content: m.content?.substring(0, 50)
      })));
      
      const existingMessageIndex = this.conversation.findIndex(msg => msg.originalItemId === event.item_id);
      
      console.log('📝 DEBUG: Found existing message at index:', existingMessageIndex);
      
      if (existingMessageIndex >= 0) {
        // Update the existing message with transcription
        const existingMessage = this.conversation[existingMessageIndex];
        console.log('📝 DEBUG: Updating existing message:', existingMessage.id);
        
        const userMessage = {
          ...existingMessage,
          content: event.transcript,
          transcription: event.transcript,
          isStreaming: false // Mark as complete so it goes to main chat
        };
        
        this.conversation[existingMessageIndex] = userMessage;
        
        console.log('📝 DEBUG: Updated message content:', userMessage.content);
        console.log('📝 DEBUG: Conversation length after update:', this.conversation.length);
        console.log('🎯✅ Updated user message with transcript:', event.transcript);
        console.log('🎯✅ Calling onMessageCallback with updated user message:', userMessage);
        this.onMessageCallback?.(userMessage);
      } else {
        console.log('🎯❌ Could not find existing message for item_id:', event.item_id);
      }
    } else {
      console.log('🎯❌ No transcript or item_id in event:', event);
    }
  }

  handleInputAudioTranscriptionFailed(event) {
    console.log('❌ Input audio transcription failed:', event);
    
    // Keep the [Voice Input] placeholder if transcription fails
    if (event.item_id) {
      const message = this.conversation.find(msg => msg.originalItemId === event.item_id);
      if (message) {
        message.content = '[Voice Input - Transcription Failed]';
        this.onMessageCallback?.(message);
      }
    }
  }

  handleRateLimitsUpdated(event) {
    console.log('⏱️ Rate limits updated:', event.rate_limits);
    // Handle rate limit updates
    // Could show user warnings if approaching limits
  }

  handleError(event) {
    console.error('❌ OpenAI Realtime API error:', event);
    this.onErrorCallback?.(new Error(event.error?.message || 'OpenAI API error'));
  }

  extractContentFromItem(item) {
    if (!item.content) return '';
    
    return item.content.map(part => {
      if (part.type === 'text') return part.text;
      if (part.type === 'audio') return '[Audio]';
      if (part.type === 'input_text') return part.text;
      if (part.type === 'input_audio') return '[Voice Input]';
      return '';
    }).join(' ');
  }

  // Send a text message to the conversation
  sendTextMessage(text) {
    if (!text?.trim()) return false;
    
    return this.sendEvent({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [
          {
            type: 'input_text',
            text: text.trim()
          }
        ]
      }
    });
  }

  // Trigger a response from the assistant
  createResponse(options = {}) {
    return this.sendEvent({
      type: 'response.create',
      response: {
        modalities: ['text', 'audio'],
        ...options
      }
    });
  }

  // Cancel current response (for interruption)
  cancelResponse(responseId = null) {
    if (responseId) {
      return this.sendEvent({
        type: 'response.cancel',
        response_id: responseId
      });
    } else {
      // Cancel all pending responses
      for (const [id] of this.pendingResponses) {
        this.sendEvent({
          type: 'response.cancel',
          response_id: id
        });
      }
      return true;
    }
  }

  // Truncate conversation item (useful for context management)
  truncateItem(itemId, contentIndex) {
    return this.sendEvent({
      type: 'conversation.item.truncate',
      item_id: itemId,
      content_index: contentIndex
    });
  }

  // Delete conversation item
  deleteItem(itemId) {
    return this.sendEvent({
      type: 'conversation.item.delete',
      item_id: itemId
    });
  }

  // Send initial greeting
  async sendGreetingMessage(text) {
    if (!text?.trim()) return;
    
    console.log('👋 Sending greeting message:', text);
    
    // Add greeting as assistant message
    const success = this.sendEvent({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'assistant',
        content: [
          {
            type: 'text',
            text: text.trim()
          }
        ]
      }
    });

    if (success) {
      // Create response to speak the greeting
      this.createResponse();
    }
  }

  // Configuration methods
  setModel(model) {
    // Map unsupported models to supported ones for Realtime API
    const realtimeCompatibleModels = {
      'gpt-5': 'gpt-4o-realtime-preview-2024-12-17',
      'gpt-4o': 'gpt-4o-realtime-preview-2024-12-17',
      'gpt-4o-mini': 'gpt-4o-realtime-preview-2024-12-17',
      'gpt-4': 'gpt-4o-realtime-preview-2024-12-17'
    };
    
    // Use compatible model if the requested model isn't supported
    if (realtimeCompatibleModels[model]) {
      console.log(`🔄 Mapping unsupported model "${model}" to "${realtimeCompatibleModels[model]}" for Realtime API`);
      this.selectedModel = realtimeCompatibleModels[model];
    } else if (model.includes('gpt-4o-realtime') || model.includes('gpt-4o-audio')) {
      // Already a supported model
      this.selectedModel = model;
    } else {
      // Default fallback for any unknown model
      console.log(`🔄 Using default model for unknown model "${model}"`);
      this.selectedModel = 'gpt-4o-realtime-preview-2024-12-17';
    }
  }

  setVoice(voice) {
    this.selectedVoice = voice;
    
    // Update session if already connected
    if (this.isConnected) {
      this.updateSession({ voice });
    }
  }

  setUser(user) {
    this.currentUser = user;
  }

  // Update session configuration
  updateSession(partialConfig = {}) {
    const sessionUpdate = {
      instructions: this.getInstructions(),
      voice: this.selectedVoice,
      ...this.sessionConfig,
      ...partialConfig
    };

    return this.sendEvent({
      type: 'session.update',
      session: sessionUpdate
    });
  }

  // Configure turn detection settings
  setTurnDetection(config) {
    this.sessionConfig.turn_detection = {
      ...this.sessionConfig.turn_detection,
      ...config
    };
    
    if (this.isConnected) {
      this.updateSession({ turn_detection: this.sessionConfig.turn_detection });
    }
  }

  // Configure audio settings
  setAudioFormat(inputFormat = 'pcm16', outputFormat = 'pcm16') {
    this.sessionConfig.input_audio_format = inputFormat;
    this.sessionConfig.output_audio_format = outputFormat;
    
    if (this.isConnected) {
      this.updateSession({
        input_audio_format: inputFormat,
        output_audio_format: outputFormat
      });
    }
  }

  // Configure AI response settings
  setResponseConfig(config) {
    if (config.temperature !== undefined) {
      this.sessionConfig.temperature = config.temperature;
    }
    if (config.max_response_output_tokens !== undefined) {
      this.sessionConfig.max_response_output_tokens = config.max_response_output_tokens;
    }
    
    if (this.isConnected) {
      this.updateSession({
        temperature: this.sessionConfig.temperature,
        max_response_output_tokens: this.sessionConfig.max_response_output_tokens
      });
    }
  }

  // Get available voice options
  getAvailableVoices() {
    return [
      { id: 'alloy', name: 'Alloy', description: 'Neutral, balanced voice' },
      { id: 'echo', name: 'Echo', description: 'Male voice with clear diction' },
      { id: 'fable', name: 'Fable', description: 'British accent, storytelling voice' },
      { id: 'onyx', name: 'Onyx', description: 'Deep, authoritative voice' },
      { id: 'nova', name: 'Nova', description: 'Young, energetic voice' },
      { id: 'shimmer', name: 'Shimmer', description: 'Soft, whispery voice' }
    ];
  }

  // Get current session configuration
  getSessionConfig() {
    return {
      model: this.selectedModel,
      voice: this.selectedVoice,
      ...this.sessionConfig,
      instructions: this.getInstructions()
    };
  }

  setInitialConversation(conversation) {
    // Preserve any pending voice messages that haven't been transcribed yet
    const pendingVoiceMessages = this.conversation ? this.conversation.filter(msg => 
      msg.role === 'user' && 
      msg.content === '[Voice Input]' && 
      msg.originalItemId && 
      !this.completedItems.has(msg.originalItemId)
    ) : [];
    
    this.conversation = [...conversation, ...pendingVoiceMessages];
    console.log('📚 Set initial conversation:', conversation.length, 'messages, preserved', pendingVoiceMessages.length, 'pending voice messages');
  }
  
  // Add conversation history to OpenAI session
  addHistoryToSession() {
    if (this.conversation && this.conversation.length > 0) {
      console.log('📚 Adding conversation history to OpenAI session:', this.conversation.length, 'messages');
      
      // Add each historical message to the session (skip system messages and voice call markers)
      this.conversation.forEach(msg => {
        if (msg.isFromHistory && msg.role !== 'system' && !msg.isCallMarker) {
          // Use appropriate content type based on role
          const contentType = msg.role === 'user' ? 'input_text' : 'text';
          
          const success = this.sendEvent({
            type: 'conversation.item.create',
            item: {
              type: 'message',
              role: msg.role,
              content: [
                {
                  type: contentType,
                  text: msg.content || msg.transcription || ''
                }
              ]
            }
          });
          
          if (success) {
            console.log(`📚 Added ${msg.role} message to session:`, (msg.content || msg.transcription || '').substring(0, 100));
          }
        }
      });
    }
  }

  // Cleanup
  cleanup() {
    console.log('🧹 Cleaning up OpenAI Realtime service...');
    
    try {
      // Close data channel
      if (this.dataChannel) {
        this.dataChannel.close();
        this.dataChannel = null;
      }
      
      // Close peer connection
      if (this.peerConnection) {
        this.peerConnection.close();
        this.peerConnection = null;
      }
      
      // Stop local media stream
      if (this.localStream) {
        this.localStream.getTracks().forEach(track => track.stop());
        this.localStream = null;
      }
      
      // Clean up audio element
      if (this.audioElement) {
        this.audioElement.srcObject = null;
        this.audioElement = null;
      }
      
      // Clean up AudioContext
      if (this.mediaStreamSource) {
        this.mediaStreamSource.disconnect();
        this.mediaStreamSource = null;
      }
      
      if (this.gainNode) {
        this.gainNode.disconnect();
        this.gainNode = null;
      }
      
      if (this.audioContext && this.audioContext.state !== 'closed') {
        this.audioContext.close();
        this.audioContext = null;
      }
      
      this.isConnected = false;
      this.isInitialized = false;
      this.audioNeedsEnabling = false;
      this.userHasEnabledAudio = false;
      
      // Clear tracking sets
      this.truncatedItems.clear();
      this.completedItems.clear();
      
    } catch (error) {
      console.error('❌ Error during cleanup:', error);
    }
  }

  // Manual audio control to overcome browser autoplay restrictions
  enableAudio() {
    console.log('🔊 Manually enabling audio system');
    
    // First, try to resume AudioContext if we have one
    if (this.audioContext) {
      console.log('🔊 Attempting to resume AudioContext');
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume().then(() => {
          console.log('🔊 AudioContext resumed successfully');
          this.audioNeedsEnabling = false;
          this.userHasEnabledAudio = true;
          
          // If we're using AudioContext playback, we're done
          if (this.mediaStreamSource && this.gainNode) {
            console.log('🔊 Audio should now be playing through AudioContext');
            return;
          }
        }).catch(error => {
          console.warn('🔊 Could not resume AudioContext:', error);
          this.tryAudioElementPlayback();
        });
      } else if (this.audioContext.state === 'running') {
        console.log('🔊 AudioContext already running');
        this.audioNeedsEnabling = false;
        this.userHasEnabledAudio = true;
        
        if (this.mediaStreamSource && this.gainNode) {
          console.log('🔊 Audio should be playing through AudioContext');
          return;
        }
      }
    }
    
    // Fallback to audio element
    this.tryAudioElementPlayback();
  }
  
  tryAudioElementPlayback() {
    if (this.audioElement) {
      console.log('🔊 Attempting audio element playback');
      console.log('🔊 Audio element state:', {
        paused: this.audioElement.paused,
        volume: this.audioElement.volume,
        muted: this.audioElement.muted,
        readyState: this.audioElement.readyState,
        src: this.audioElement.src,
        srcObject: !!this.audioElement.srcObject,
        currentTime: this.audioElement.currentTime,
        duration: this.audioElement.duration
      });
      
      // Ensure volume and unmuted
      this.audioElement.volume = 1.0;
      this.audioElement.muted = false;
      
      // Force play the audio element
      this.audioElement.play().then(() => {
        console.log('🔊 Audio element enabled successfully');
        this.audioNeedsEnabling = false;
        this.userHasEnabledAudio = true;
      }).catch((error) => {
        console.warn('🔊 Could not enable audio element:', error);
        
        // Try creating a new AudioContext as last resort
        this.tryCreateNewAudioContext();
      });
    } else {
      console.warn('🔊 No audio element found');
    }
  }
  
  tryCreateNewAudioContext() {
    try {
      console.log('🔊 Attempting to create new AudioContext as fallback');
      const newAudioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      if (newAudioContext.state === 'suspended') {
        newAudioContext.resume().then(() => {
          console.log('🔊 New AudioContext resumed');
          this.audioNeedsEnabling = false;
          this.userHasEnabledAudio = true;
        }).catch(error => {
          console.warn('🔊 Could not resume new AudioContext:', error);
        });
      } else {
        console.log('🔊 New AudioContext created and running');
        this.audioNeedsEnabling = false;
        this.userHasEnabledAudio = true;
      }
    } catch (error) {
      console.warn('🔊 Could not create new AudioContext:', error);
    }
  }

  // Debug method to check audio system
  debugAudioSystem() {
    console.log('🔊 Audio System Debug:', {
      hasAudioElement: !!this.audioElement,
      isConnected: this.isConnected,
      isInitialized: this.isInitialized,
      isAudioPlaying: this.isAudioPlaying,
      peerConnectionState: this.peerConnection?.connectionState,
      dataChannelState: this.dataChannel?.readyState
    });
    
    if (this.audioElement) {
      console.log('🔊 Audio Element Debug:', {
        paused: this.audioElement.paused,
        volume: this.audioElement.volume,
        muted: this.audioElement.muted,
        readyState: this.audioElement.readyState,
        srcObject: !!this.audioElement.srcObject,
        currentTime: this.audioElement.currentTime,
        duration: this.audioElement.duration
      });
      
      if (this.audioElement.srcObject) {
        const stream = this.audioElement.srcObject;
        console.log('🔊 Stream Debug:', {
          active: stream.active,
          id: stream.id,
          audioTracks: stream.getAudioTracks().length,
          tracks: stream.getTracks().map(t => ({
            kind: t.kind,
            enabled: t.enabled,
            muted: t.muted,
            readyState: t.readyState
          }))
        });
      }
    }
  }

  // Status getters
  isReady() {
    return this.isInitialized && this.isConnected;
  }

  getConversation() {
    return this.conversation;
  }

  clearConversation() {
    this.conversation = [];
    // Also clear tracking sets when conversation is cleared
    this.truncatedItems.clear();
    this.completedItems.clear();
  }
}

// Export singleton instance
export const openaiRealtimeService = new OpenAIRealtimeService();